import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMemoizedFn } from 'ahooks';
import {
  render as reactRender,
  unmount as reactUnmount,
} from 'rc-util/lib/React/render';
import { ConfigProvider, Modal, Theme } from '@bedrock/components';
// @ts-ignore
import { ModalProps } from '@bedrock/components/lib/Modal';
import {
  ScreenAdapterContext,
  useScreenAdapterContext,
} from '@bedrock/eed-react';

const IS_GREATER_THEN_REACT_16 = !!createPortal;

const destroyFns = [] as Array<() => void>;

// 打开弹框方法
function openModal(config: ModalProps, Provider: any) {
  const container = document.createDocumentFragment();
  let isOpen = false;

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  let currentConfig = { ...config, visible: true };

  function render(props: any) {
    reactRender(
      <Provider>
        <Modal {...props} />
      </Provider>,
      container
    );
  }

  function destroy(...args: [any]) {
    if (!isOpen) return;
    isOpen = false;
    reactUnmount(container);
    const triggerCancel = args.some((param) => param && param.triggerCancel);

    if (config.onCancel && triggerCancel) {
      config.onCancel(...args);
    }

    const destroyIndex = destroyFns.indexOf(close);
    if (destroyIndex !== -1) {
      destroyFns.splice(destroyIndex, 1);
    }
  }

  const destroyWrap = (...args: any[]) => {
    setTimeout(destroy.bind(undefined, ...args), 0);
  };

  function close(...args: any[]) {
    currentConfig = {
      ...currentConfig,
      visible: false,
      // @ts-ignore
      afterClose: () => {
        if (typeof config.afterClose === 'function') {
          config.afterClose();
        }
        destroyWrap.apply(undefined, args);
      },
    };

    if (IS_GREATER_THEN_REACT_16) {
      render(currentConfig);
    } else {
      destroyWrap(...args);
    }
  }

  function update(nextConfig: ModalProps) {
    if (!isOpen) return;
    currentConfig = {
      ...currentConfig,
      ...nextConfig,
    };

    render(currentConfig);
  }

  destroyFns.push(close);

  isOpen = true;
  render(currentConfig);

  return { destroy: close, update };
}

openModal.destroyAll = () => {
  while (destroyFns.length) {
    const close = destroyFns.pop();
    close && close();
  }
};

export function useOpenModal<T>(
  config: {
    onOk: () => Promise<T> | T;
  } & Omit<ModalProps, 'onOk'>
) {
  const dialogRef = React.useRef<any>({});
  const promiseRef = React.useRef<any>({});
  const screenAdapter = useScreenAdapterContext();

  const [OKLoading, setOkLoading] = useState(false);

  // const ref = React.useRef<any>(null)

  const modalConfig = React.useMemo(() => {
    return Object.assign({}, config, {
      okButtonProps: {
        loading: OKLoading,
        ...(config.okButtonProps || {}),
      },
      onOk: (...args: any[]) => {
        if (config.onOk) {
          // @ts-ignore
          setOkLoading(true);
          Promise.resolve(config.onOk(...args))
            .then((res) => {
              promiseRef.current.resolve(res);
            })
            .finally(() => {
              setOkLoading(false);
            });
        } else {
          promiseRef.current.resolve(args);
        }
      },
      onCancel: (...args: any[]) => {
        if (config.onCancel) {
          // @ts-ignore
          Promise.resolve(config.onCancel(...args)).then((res) => {
            promiseRef.current.reject(res);
          });
        } else {
          promiseRef.current.reject(args);
        }
      },
    });
  }, [config, OKLoading]);

  // 参数变化时新更新；
  React.useEffect(() => {
    if (dialogRef.current?.update) {
      dialogRef.current?.update?.(modalConfig);
    }
  }, [modalConfig]);

  React.useEffect(() => {
    return () => {
      if (process.env.NODE_ENV !== 'development') {
        dialogRef?.current?.destroy?.();
      }
    };
  }, []);

  const openPromiseRef = useRef<any>(null);

  const open = useMemoizedFn(() => {
    if (openPromiseRef.current) {
      return openPromiseRef.current;
    }
    const promise: Promise<T> = new Promise((resolve, reject) => {
      const Provider = (props) => (
        <ScreenAdapterContext.Provider value={screenAdapter}>
          <ConfigProvider
            ratio={screenAdapter?.viewBase} // 设置组件内部的ratio，影响组件内部样式；
          >
            <Theme>{props.children}</Theme>
          </ConfigProvider>
        </ScreenAdapterContext.Provider>
      );
      dialogRef.current = openModal(modalConfig, Provider);
      promiseRef.current = { resolve, reject };
    });
    openPromiseRef.current = promise;
    promise.finally(() => {
      openPromiseRef.current = null;
      // 关闭弹框，销毁
      dialogRef?.current?.destroy?.();
    });
    return promise;
  });
  const destroy = useMemoizedFn(() => {
    dialogRef.current?.destroy?.();
    dialogRef.current = null;
    openPromiseRef.current = null;
  });

  return {
    open,
    destroy,
  };
}
